import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import css from "./Textarea.module.css";

const Textarea = React.forwardRef(
  (
    {
      bare = false,
      label,
      description,
      error,
      disabled = false,
      required = false,
      name,
      placeholder,
      value,
      defaultValue,
      onValueChange,
      rows = 3,
      autoGrow = false,
      resize = "vertical",
      validate,
      validationMode,
      validationDebounceTime,
      className,
      ...rest
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    const handleInput = autoGrow
      ? (e) => {
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = `${el.scrollHeight}px`;
        }
      : undefined;

    const handleChange = onValueChange
      ? (e) => onValueChange(e.target.value)
      : undefined;

    if (bare) {
      return (
        <Field.Control
          ref={ref}
          render={<textarea />}
          className={clsx(css.textarea, autoGrow && css.autoGrow)}
          style={{ resize: autoGrow ? "none" : resize }}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onInput={handleInput}
          required={required}
          disabled={disabled}
          rows={rows}
          {...rest}
        />
      );
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
        validate={validate}
        validationMode={validationMode}
        validationDebounceTime={validationDebounceTime}
      >
        {label && <Field.Label className={css.label}>{label}</Field.Label>}
        <Field.Control
          ref={ref}
          render={<textarea />}
          className={clsx(css.textarea, autoGrow && css.autoGrow)}
          style={{ resize: autoGrow ? "none" : resize }}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onInput={handleInput}
          required={required}
          rows={rows}
          {...rest}
        />
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

Textarea.displayName = "Textarea";

Textarea.propTypes = {
  bare: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  rows: PropTypes.number,
  autoGrow: PropTypes.bool,
  resize: PropTypes.oneOf(["vertical", "none", "both"]),
  validate: PropTypes.func,
  validationMode: PropTypes.string,
  validationDebounceTime: PropTypes.number,
  className: PropTypes.string,
};

export default Textarea;
